import { useCallback, useEffect, useRef, useState } from 'react'
import { nextRate, type SpeechRate } from './speech'
import { SpeechPlayback } from './speechPlayback'
import { motionReduced } from './preferences'

const BLOCKS = 'h1, h2, h3, h4, p, li, blockquote, dt, dd, figcaption, address'
const RATE_KEY = 'gia:a11y:rate'
const READING_ATTR = 'data-a11y-reading'

export type ReadAloudStatus = 'idle' | 'starting' | 'playing' | 'paused'

/** Visible, non-nested text blocks inside <main>, in reading order. */
function collectBlocks(): HTMLElement[] {
  const main = document.getElementById('main-content')
  if (!main) return []
  return Array.from(main.querySelectorAll<HTMLElement>(BLOCKS)).filter((el) => {
    if (el.closest('[aria-hidden="true"], .sr-only, [hidden], form, [data-read-aloud-ignore]')) return false
    const outer = el.parentElement?.closest(BLOCKS)
    if (outer && main.contains(outer)) return false // read the outer block once
    const style = getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden') return false
    return (el.innerText || '').trim().length > 1
  })
}

/** First block that is at or below the top of the viewport (under the header). */
function firstVisibleIndex(blocks: HTMLElement[]): number {
  const header = document.querySelector('header')?.getBoundingClientRect().bottom ?? 0
  const i = blocks.findIndex((el) => el.getBoundingClientRect().bottom > header + 8)
  return i === -1 ? 0 : i
}

function loadRate(): SpeechRate {
  try {
    const n = Number(localStorage.getItem(RATE_KEY))
    return ([1, 1.25, 1.5, 0.8] as number[]).includes(n) ? (n as SpeechRate) : 1
  } catch {
    return 1
  }
}

/**
 * Read the page aloud with the Web Speech API, block by block, highlighting
 * and scrolling to the block being read. Starts where the visitor is on the
 * page. Pause cancels and later restarts the current block (more reliable
 * across browsers than speechSynthesis.pause()).
 */
export function useReadAloud() {
  const [supported, setSupported] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<ReadAloudStatus>('idle')
  const [position, setPosition] = useState({ index: 0, total: 0 })
  const [rate, setRateState] = useState<SpeechRate>(1)

  const blocks = useRef<HTMLElement[]>([])
  const index = useRef(0)
  const playback = useRef<SpeechPlayback | null>(null)
  const rateRef = useRef<SpeechRate>(1)

  useEffect(() => {
    const available = 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window
    setSupported(available)
    if (available) playback.current = new SpeechPlayback(window.speechSynthesis)
    const r = loadRate()
    rateRef.current = r
    setRateState(r)
  }, [])

  const highlight = useCallback((el: HTMLElement | null) => {
    document.querySelectorAll(`[${READING_ATTR}]`).forEach((n) => n.removeAttribute(READING_ATTR))
    if (!el) return
    el.setAttribute(READING_ATTR, '')
    const r = el.getBoundingClientRect()
    if (r.top < 80 || r.bottom > window.innerHeight - 120) {
      el.scrollIntoView({ block: 'center', behavior: motionReduced() ? 'auto' : 'smooth' })
    }
  }, [])

  const speakFrom = useCallback(
    (start: number) => {
      const list = blocks.current
      if (start < 0 || start >= list.length || !playback.current) {
        highlight(null)
        setStatus('idle')
        return
      }
      setError(null)
      setStatus('starting')
      playback.current.play({
        blocks: list.map((element) => element.innerText),
        start,
        lang: document.documentElement.lang || 'en-US',
        rate: rateRef.current,
        onBlock: (current) => {
          index.current = current
          setPosition({ index: current, total: list.length })
          highlight(list[current])
        },
        onStart: () => setStatus('playing'),
        onComplete: () => {
          highlight(null)
          setStatus('idle')
        },
        onError: (message) => {
          highlight(null)
          setStatus('idle')
          setError(message)
        },
      })
    },
    [highlight],
  )

  const start = useCallback(() => {
    if (!supported) return
    blocks.current = collectBlocks()
    if (!blocks.current.length) return
    // Stay inside the click/tap: a timer here loses mobile audio permission.
    speakFrom(firstVisibleIndex(blocks.current))
  }, [supported, speakFrom])

  const stop = useCallback(() => {
    playback.current?.stop()
    setError(null)
    highlight(null)
    setStatus('idle')
  }, [highlight])

  const pause = useCallback(() => {
    playback.current?.stop()
    setStatus('paused')
  }, [])

  const resume = useCallback(() => speakFrom(index.current), [speakFrom])
  const next = useCallback(() => speakFrom(Math.min(index.current + 1, blocks.current.length - 1)), [speakFrom])
  const previous = useCallback(() => speakFrom(Math.max(index.current - 1, 0)), [speakFrom])

  const cycleRate = useCallback(() => {
    const r = nextRate(rateRef.current)
    rateRef.current = r
    setRateState(r)
    try {
      localStorage.setItem(RATE_KEY, String(r))
    } catch {
      // not persisted; still applies now
    }
    if (status === 'playing' || status === 'starting') speakFrom(index.current) // restart the block at the new speed
  }, [status, speakFrom])

  // Never keep talking after the visitor leaves the page.
  useEffect(() => {
    const onHide = () => {
      playback.current?.stop()
      setStatus('idle')
      document.querySelectorAll(`[${READING_ATTR}]`).forEach((n) => n.removeAttribute(READING_ATTR))
      document.documentElement.removeAttribute('data-a11y-speaking')
    }
    window.addEventListener('pagehide', onHide)
    return () => {
      window.removeEventListener('pagehide', onHide)
      onHide()
    }
  }, [])

  // Signal the rest of the UI (e.g. hide the back-to-top button while the bar shows).
  useEffect(() => {
    const root = document.documentElement
    if (status === 'idle') root.removeAttribute('data-a11y-speaking')
    else root.setAttribute('data-a11y-speaking', 'true')
  }, [status])

  return { supported, status, error, position, rate, start, stop, pause, resume, next, previous, cycleRate }
}
